// Pemetaan baris DB (snake_case) → bentuk domain Ticket (dipakai domain/money.ts).
import { supabase } from './supabase';
import type { Ticket, PaymentRound, TicketStatus, Hasil, Refund } from './types';

export interface TicketRow {
  no: string; customer_id: number; outlet: string; status: string; hasil?: string | null;
  biaya?: number | null; dp?: number | null; modal?: number | null;
  lunas?: boolean | null; verified?: boolean | null; cara_bayar?: string | null;
  est_biaya?: number | null; tgl_iso?: string | null; tgl_ambil_iso?: string | null; jatuh_tempo?: string | null;
  biaya_simpan?: number | null; refund?: unknown;
  archived?: boolean | null; arsip_keu?: boolean | null; teknisi?: string | null;
}

export function rowToTicket(r: TicketRow, rounds: PaymentRound[] = []): Ticket {
  return {
    no: r.no, cust: r.customer_id, outlet: r.outlet,
    status: r.status as TicketStatus, hasil: (r.hasil ?? undefined) as Hasil,
    biaya: r.biaya ?? 0, dp: r.dp ?? 0, modal: r.modal ?? 0,
    lunas: r.lunas ?? false, verified: r.verified ?? false,
    caraBayar: r.cara_bayar ?? undefined, estBiaya: r.est_biaya ?? 0,
    tglISO: r.tgl_iso ?? undefined,
    // Saat unit sedang diklaim garansi, `servisUlang` mengosongkan tgl_ambil_iso dan memindahkan
    // tanggal ambil sesi lama ke ticket_rounds. Tanpa cadangan ini tiketnya hilang dari SEMUA
    // penyaringan periode — uang yang sudah diterima bulan lalu lenyap dari Dashboard & grafik
    // Tren, sementara Laba-Rugi (punya cadangan sendiri) tetap menghitungnya. Satu tiket, dua
    // angka berbeda di dua layar.
    tglAmbilISO: r.tgl_ambil_iso ?? [...rounds].reverse().find((x) => x.tglAmbilISO)?.tglAmbilISO ?? undefined,
    jatuhTempo: r.jatuh_tempo ?? undefined,
    biayaSimpan: r.biaya_simpan ?? 0, refund: (r.refund ?? null) as Refund | null,
    riwayatServis: rounds,
    archived: r.archived ?? false, arsipKeu: r.arsip_keu ?? false,
    teknisi: r.teknisi ?? undefined,
  };
}

/** Kolom tiket yang diambil untuk perhitungan uang (dipakai .select()). */
export const MONEY_COLS =
  'no,customer_id,outlet,status,hasil,biaya,dp,modal,lunas,verified,cara_bayar,est_biaya,tgl_iso,tgl_ambil_iso,jatuh_tempo,biaya_simpan,refund,archived,arsip_keu,teknisi';

interface RoundRow {
  ticket_no: string; biaya: number | null; dp: number | null; modal: number | null;
  lunas: boolean | null; verified: boolean | null; biaya_simpan: number | null; refund: unknown;
}

/** Ambil ronde riwayat (ticket_rounds) untuk sekumpulan tiket → Map no → PaymentRound[].
 *  Wajib dipanggil sebelum hitung uang agar tiket servis-ulang (multi-sesi) benar. */
export async function fetchRounds(nos: string[]): Promise<Map<string, PaymentRound[]>> {
  const m = new Map<string, PaymentRound[]>();
  if (!nos.length) return m;
  // Dipecah dua kali: `.in(...)` masuk ke URL (ribuan nomor → permintaan ditolak karena
  // kepanjangan), dan hasilnya sendiri dipotong PostgREST di 1.000 baris tanpa tanda.
  const rows: RoundRow[] = [];
  const PAGE = 1000;
  for (let i = 0; i < nos.length; i += 300) {
    const bagian = nos.slice(i, i + 300);
    for (let dari = 0; ; dari += PAGE) {
      const { data, error } = await supabase()
        .from('ticket_rounds')
        .select('ticket_no,ronde,biaya,dp,modal,lunas,verified,biaya_simpan,refund,data')
        .in('ticket_no', bagian).order('ticket_no').order('ronde').range(dari, dari + PAGE - 1);
      if (error) throw new Error(error.message);
      const hal = (data ?? []) as unknown as RoundRow[];
      rows.push(...hal);
      if (hal.length < PAGE) break;
    }
  }
  for (const r of rows) {
    const arr = m.get(r.ticket_no) ?? [];
    arr.push({
      biaya: r.biaya ?? 0, dp: r.dp ?? 0, modal: r.modal ?? 0,
      lunas: r.lunas ?? false, verified: r.verified ?? false, biayaSimpan: r.biaya_simpan ?? 0,
      refund: (r.refund ?? null) as Refund | null,
      // Tanggal ambil sesi ini — dipakai laporan sbg CADANGAN saat tiket sedang servis-ulang
      // (kolom tgl_ambil_iso di `tickets` direset kosong, lihat queue.repo.servisUlang).
      tglAmbilISO: (r as { data?: { tgl_ambil_iso?: string | null } }).data?.tgl_ambil_iso ?? null,
    });
    m.set(r.ticket_no, arr);
  }
  return m;
}
